import React from "react";
import { Box, Fab, FabProps } from "@mui/material";

export interface CenteredFabProps {
  /** The icon to show inside the FAB */
  icon: React.ReactNode;
  /** Accessible label */
  label: string;
  /** Color of the FAB */
  color?: FabProps["color"];
  /** Size of the FAB */
  size?: FabProps["size"];
  /** Called when the FAB is clicked */
  onClick?: () => void;
  /** Position of the FAB inside the container */
  position?: "start" | "center" | "end";
  /** Custom styles for the FAB */
  sx?: any;
}

/**
 * A floating action button centered horizontally with configurable position.
 */
export const CenteredFab: React.FC<CenteredFabProps> = ({
  icon,
  label,
  color = "primary",
  size = "medium",
  onClick,
  position = "end",
  sx,
}) => {
  // Map position prop to justifyContent
  const justifyContentMap: Record<
    NonNullable<CenteredFabProps["position"]>,
    string
  > = {
    start: "flex-start",
    center: "center",
    end: "flex-end",
  };

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 84,
        left: "45%",
        transform: "translateX(-50%)",
        width: "100%",
        maxWidth: "500px",
        display: "flex",
        justifyContent: justifyContentMap[position],
        px: 2,
        zIndex: (theme) => 400,
        pointerEvents: "none", // Allow clicks to pass through the container
      }}
    >
      <Fab
        size={size}
        color={color}
        aria-label={label}
        onClick={onClick}
        sx={{ 
          pointerEvents: "auto", // Ensure FAB can receive clicks
          ...sx // Merge custom styles
        }}
      >
        {icon}
      </Fab>
    </Box>
  );
};

export default CenteredFab;
